import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import styles from "./scheduledPosts.module.scss";
import cx from "classnames";
import Slider from "src/components/slider";
import { Dropdown, Image } from "react-bootstrap";
import _ from "lodash";
import Button from "src/components/button";
import { useDispatch } from "react-redux";
import { deleteLinkedInScheduledPostRequest, fetchLinkedInPosts, invalidateLinkedInPosts } from "src/redux/actions/integrations/linkedIn";
import EmptyMessage from "src/components/emptyMessage";
import InfiniteScroll from "src/components/infiniteScroll";
import Truncate from "src/components/truncate";

const DefaultAssetIcon = "/images/defaultIcons/default_asset.jpg";

interface ScheduledPostsProps {
  onClose: () => void;
}

export const ScheduledPosts: React.FC<ScheduledPostsProps> = ({ onClose }) => {
  const scheduledPosts = useSelector((state: any) => _.get(state, "integrations.linkedIn.posts.items", []));
  const pagination = useSelector((state: any) => _.get(state, "integrations.linkedIn.posts.pagination", { currentPage: 0, totalPages: 0 }));
  const loading = useSelector((state: any) => _.get(state, "integrations.linkedIn.loading", false));
  const hasMoreItems = pagination ? (pagination.currentPage + 1) < pagination.totalPages : false;

  const options = [
    { label: "All Posts", value: "all" },
    { label: "Scheduled", value: "post_requested" },
    { label: "Published", value: "posted" },
    { label: "Failed", value: "error" },
  ];

  const dispatch = useDispatch();

  const [selectedOption, setSelectedOption] = React.useState<any>(options[0]);
  const [deletePostId, setDeletePostId] = React.useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchLinkedInPosts({ page: 0, limit: 20, postStatus: "all" }));
  }, []);

  const className = "";

  const placeholder = "Select an option";

  const handleSelect = (value: "all" | "post_requested" | "posted" | "error") => {
    const option = options.find((opt) => opt.value === value);
    setSelectedOption(option);
    dispatch(invalidateLinkedInPosts())
    dispatch(fetchLinkedInPosts({ page: 0, limit: 20, postStatus: value }));
  };

  const loadMorePosts = () => {
    if (!loading && hasMoreItems) {
      dispatch(fetchLinkedInPosts({ page: pagination.currentPage + 1, limit: 20, postStatus: selectedOption.value }));
    }
  }

  return (
    <div className={styles.scheduledPostsContainer}>
      <Slider.Header
        title="scheduled posts"
        icon="paperflite-left-arrow"
        handleClick={onClose}
      />

      <div className={styles.body}>
        <div className={styles.filter}>
          <Dropdown
            className={cx(styles.selectDropdown, className)}
            placement="bottom"
          >
            <Dropdown.Toggle>
              {selectedOption && (
                <>
                  {selectedOption?.icon && (
                    <img src={selectedOption.icon} alt={selectedOption.label} />
                  )}
                  {selectedOption?.label}
                </>
              )}

              {!selectedOption && placeholder}
              {_.size(options) > 1 && (
                <i
                  className={cx("paperflite-dropdown", styles.dropdownIcon)}
                ></i>
              )}
            </Dropdown.Toggle>

            {_.size(options) > 1 && (
              <Dropdown.Menu
                popperConfig={{
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 6],
                      },
                    },
                  ],
                }}
              >
                {options.map((option, index) => {
                  const selected = option.value === selectedOption?.value;
                  return (
                    <Dropdown.Item
                      key={index}
                      className={cx({ [styles.active]: selected })}
                      onClick={() => handleSelect(option.value as "all" | "post_requested" | "posted" | "error")}
                    >
                      <span>{option.label}</span>
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            )}
          </Dropdown>
        </div>
        <div className={styles.postsList}>
          {_.size(scheduledPosts) === 0 && !loading ? (
            <div className={styles.noPostsMessage}>
             <EmptyMessage className={styles.emptyMessage} image={"/images/emptyTimeLine.svg"} message="No record(s) found"/>
            </div>
          ) : (
            <InfiniteScroll
              className={styles.scroll}
              loading={loading}
              hasMore={hasMoreItems}
              loadMore={loadMorePosts}
              loader={() => (
                <>
                  <PlaceholderPostItem />
                </>
              )}
            >
              {scheduledPosts.map((post: any) => (
                <PostItem
                  key={post.id}
                  post={post}
                  deletePostId={deletePostId}
                  setDeletePostId={setDeletePostId}
                />
              ))}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
};

const PostItem: React.FC<{ post: any; deletePostId: number | null; setDeletePostId: React.Dispatch<React.SetStateAction<number | null>> }> = ({ post, deletePostId, setDeletePostId }) => {

  const dispatch = useDispatch();

  const media = _.get(post.mediaDTO, "thumbnail") || DefaultAssetIcon;
  const status = _.get(post, "status", "post_requested");

  const scheduledDate = _.get(post, "scheduledDate", "");
  const postScheduledDate = scheduledDate ? new Date(scheduledDate) : null;
  const postScheduledDay = postScheduledDate
    ? postScheduledDate.getDate().toString().padStart(2, '0')
    : '';
  const postScheduledMonth = postScheduledDate
    ? postScheduledDate.toLocaleString('default', { month: 'long' })
    : '';
  const postScheduledYear = postScheduledDate
    ? postScheduledDate.getFullYear()
    : '';
  const formattedDate = postScheduledDate
    ? `${postScheduledDay} ${postScheduledMonth} ${postScheduledYear}`
    : '';
  const postScheduledTime = postScheduledDate
    ? postScheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
    : '';
  
  return (
    <div className={cx(styles.postItem, {
      [styles.fadeItem]: deletePostId !== null && deletePostId !== post.id,
    })}>
      <div className={styles.postItemDetails}>
        <div className={styles.thumbnail}>
          <Image
            src={media}
            alt="Post Thumbnail"
            className={styles.thumbnailImage}
          />
        </div>
        <div className={styles.postContent}>
          <Truncate className={styles.postTitle} width={250} tooltipText={post.name}>
            {post.name}
          </Truncate>

          {status === "posted" && (
            <div className={cx(styles.statusMessage, styles.postedMessage)}>
              <i className="paperflite-completed-action"></i>
              Published on {formattedDate}, {postScheduledTime}
            </div>
          )}

          {status === "error" && (
            <div className={cx(styles.statusMessage, styles.failedMessage)}>
              <i className="paperflite-not-started-actions"></i>
              Failed to publish on {formattedDate}, {postScheduledTime}
            </div>
          )}

          {status === "post_requested" && (<div className={styles.metadata}>
            <div className={styles.date}>{formattedDate}</div>
            <div className={styles.separator} />
            <div className={styles.time}>{postScheduledTime}</div>
          </div>)}
        </div>

        <div className={styles.actionItems}>
          <i
            className={cx("paperflite-delete", styles.actionIcon, {
              [styles.disabledIcon]: status === "posted"
            })}
            onClick={() => { if(!deletePostId && status !== "posted") setDeletePostId(post.id) }}
          />
        </div>
      </div>

      <ConfirmDeleteModal
        show={deletePostId === post.id}
        onClose={() => {
          setDeletePostId(null);
        }}
        onConfirm={() => {
          setDeletePostId(null);
          dispatch(deleteLinkedInScheduledPostRequest({ postId: post.id }))
        }}
      />
    </div>
  );
};

const ConfirmDeleteModal: React.FC<{
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ show, onClose, onConfirm }) => {

  const deleting = useSelector((state: any) => _.get(state, "integrations.linkedIn.deleting", false));

  return (
    <div className={cx(styles.confirmDeleteModal, {
      [styles.showModal]: show,
    })}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>Are you sure you want to delete this?</div>
        <div className={styles.modalBody}>
          Once deleted, you won't be able to access the scheduled post anymore.
        </div>
        <div className={styles.modalActions}>
          <Button
            variant="primary"
            className={styles.saveButton}
            icon="paperflite-delete"
            text={deleting ? "Deleting..." : "Delete"}
            disabled={deleting}
            onClick={onConfirm}
          />
          <Button
            variant="secondary"
            className={styles.cancelButton}
            onClick={onClose}
            text="Cancel"
          />
        </div>
      </div>
    </div>
  );
};

const PlaceholderPostItem: React.FC = () => {
  return (
    <div className={styles.postItemPlaceholder}>
    <div className={styles.thumbnailPlaceholder} />
    <div className={styles.postContentPlaceholder}>
      <div className={styles.titlePlaceholder} />
      <div className={styles.metadataPlaceholder} />
    </div>
  </div>
  );
}
